import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  capitalize,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { IGroup, IOrganizationUnit, IRoom } from "../../../../types/NendaTypes";
import { ExpandMore } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import InputSelect from "../ui-components/input/InputSelect";
import ScreenSelectFilter, { FilterKey } from "./ScreenSelectFilter";

enum Grouping {
  PREMISE = "premise",
  GROUP = "group",
}

// This is a constant for ungrouped screens
const UNGROUPED_SCREENS = "ungrouped_screens";
// This is a constant for screens group by a pre mise specific group when on company level
const UNAVAILABLE_GROUP = "unavailable_group";

type ScreenSelectMode = "grouping" | "filter";

interface ScreenSelectProps {
  screens: IRoom[];
  selectedScreens: IRoom[];
  premises: IOrganizationUnit[];
  groups: IGroup[];
  mode?: ScreenSelectMode;
  onScreenSelect: (selectedScreens: IRoom[]) => void;
}

const groupScreens = (
  by: Grouping,
  screens: IRoom[],
  groups: IGroup[] = []
): Record<string, IRoom[]> => {
  const grouped = screens.reduce<Record<string, IRoom[]>>((acc, screen) => {
    let key = "";
    if (by === Grouping.PREMISE) {
      key = screen.hotel;
    } else if (by === Grouping.GROUP) {
      const group = groups.find((g) => g._id === screen.group);
      key = group
        ? group._id
        : screen.group
          ? UNAVAILABLE_GROUP
          : UNGROUPED_SCREENS;
    }
    (acc[key] ??= []).push(screen);
    return acc;
  }, {});

  // sort groups by name
  const sortedKeys = Object.keys(grouped).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true })
  );
  const sortedGroups = Object.fromEntries(
    sortedKeys.map((key) => [key, grouped[key]])
  );

  if (by === Grouping.GROUP) {
    // put ungrouped and unavailbe groups screens at the end of the list
    const ungrouped = sortedGroups[UNGROUPED_SCREENS];
    const unavailable = sortedGroups[UNAVAILABLE_GROUP];
    delete sortedGroups[UNAVAILABLE_GROUP];
    delete sortedGroups[UNGROUPED_SCREENS];
    if (unavailable) sortedGroups[UNAVAILABLE_GROUP] = unavailable;
    if (ungrouped) sortedGroups[UNGROUPED_SCREENS] = ungrouped;
  }

  // sort screens inside each bucket by name
  Object.values(sortedGroups).forEach((arr) =>
    arr.sort((a) => a.name.localeCompare(a.name, undefined, { numeric: true }))
  );

  return sortedGroups;
};

const ScreenSelect: React.FC<ScreenSelectProps> = ({
  screens,
  selectedScreens,
  onScreenSelect,
  premises,
  groups,
  mode = "grouping",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const groupingByGroupEnabled = mode === "grouping" && groups.length > 0;

  const [selectedGrouping, setSelectedGrouping] = useState<Grouping>(
    Grouping.PREMISE
  );

  const [filters, setFilters] = useState<{
    group: string[];
    premise: string[];
  }>({
    group: [],
    premise: [],
  });

  const filteredScreens = useMemo(() => {
    let list = screens;

    // GROUP facet uses AND logic
    if (filters.group.length) {
      list = list.filter((s) =>
        filters.group.every((gid) => s.groups.includes(gid))
      );
    }

    // PREMISE facet uses OR logic
    if (filters.premise.length) {
      list = list.filter((s) => filters.premise.includes(s.hotel));
    }

    return list;
  }, [screens, filters]);

  const groupedScreens = useMemo(
    () => groupScreens(selectedGrouping, filteredScreens, groups),
    [selectedGrouping, filteredScreens, groups]
  );

  const [expanded, setExpanded] = useState<string[]>(() => {
    const groupKeys = Object.keys(groupedScreens);

    return groupKeys.length === 1 ? [groupKeys[0]] : [];
  });

  useEffect(() => {
    const visibleIds = new Set(filteredScreens.map((s) => s._id));

    if (selectedScreens.some((sel) => !visibleIds.has(sel._id))) {
      onScreenSelect(selectedScreens.filter((sel) => visibleIds.has(sel._id)));
    }

    const keys = Object.keys(groupedScreens);

    setExpanded((prev) => {
      const previouslyExpanded = prev.filter((k) => keys.includes(k));
      if (previouslyExpanded.length) return previouslyExpanded;
      return keys.length === 1 ? [keys[0]] : [];
    });
  }, [filteredScreens, selectedScreens, onScreenSelect, groupedScreens]);

  const handleFilterChange = (key: FilterKey, value: string[]) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const handleSelectedGroupingChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const nextGrouping = event.target.value as Grouping;

    setSelectedGrouping(nextGrouping);
    setExpanded([]);
  };

  const handleExpand = (groupKey: string) => {
    setExpanded((prev) =>
      prev.includes(groupKey)
        ? prev.filter((h) => h !== groupKey)
        : [...prev, groupKey]
    );
  };

  const handleSelectAll = () => {
    if (selectedScreens.length === filteredScreens.length) {
      onScreenSelect([]);
    } else {
      onScreenSelect(filteredScreens);
    }
  };

  const handleSelectAllFromGroup = (groupKey: string) => {
    const screenGroup = groupedScreens[groupKey];
    const selectedScreensFromGroup = screenGroup.filter((s) =>
      selectedScreens.some((sel) => sel._id === s._id)
    );

    if (selectedScreensFromGroup.length === screenGroup.length) {
      const newScreens = selectedScreens.filter(
        (s) => !screenGroup.some((g) => g._id === s._id)
      );
      onScreenSelect(newScreens);
    } else {
      const newScreens = [...new Set([...selectedScreens, ...screenGroup])];
      onScreenSelect(newScreens);
    }
  };

  const handleScreenSelect = (screen: IRoom, selected: boolean) => {
    if (selected) {
      onScreenSelect([...selectedScreens, screen]);
    } else {
      onScreenSelect(selectedScreens.filter((s) => s._id !== screen._id));
    }
  };

  const groupedByGroupLabel = groupingByGroupEnabled
    ? groups.reduce(
        (acc, group) => {
          acc[group._id] = group.name;
          return acc;
        },
        {
          [UNGROUPED_SCREENS]: t(
            "customerportal.pages.promotions.ungrouped_screens"
          ),
          [UNAVAILABLE_GROUP]: t(
            "customerportal.pages.promotions.unavailable_group"
          ),
        } as Record<string, string>
      )
    : undefined;
  const groupedByPremiseLabel = premises.reduce(
    (acc, premise) => {
      acc[premise._id] = premise.name;
      return acc;
    },
    {} as Record<string, string>
  );

  const groupedByLabel =
    selectedGrouping === Grouping.GROUP
      ? groupedByGroupLabel
      : groupedByPremiseLabel;

  return (
    <Box sx={{ width: "100%", minHeight: "500px" }}>
      <Stack
        direction={isMobile ? "column" : "row"}
        gap={0.5}
        justifyContent={
          isMobile
            ? "flex-start"
            : groupingByGroupEnabled || mode === "filter"
              ? "space-between"
              : "flex-end"
        }
        alignItems="center"
        py={1}
      >
        {groupingByGroupEnabled && (
          <InputSelect
            id="status"
            value={selectedGrouping}
            title={t("common.group_by")}
            defaultValue={selectedGrouping}
            onChange={handleSelectedGroupingChange}
            variant="standard"
            sx={{ fontSize: "1rem", textTransform: "capitalize" }}
          >
            <MenuItem value={Grouping.PREMISE} key={Grouping.PREMISE}>
              {capitalize(t("glossary.premise_one"))}
            </MenuItem>
            <MenuItem value={Grouping.GROUP} key={Grouping.GROUP}>
              {capitalize(t("glossary.group_one"))}
            </MenuItem>
          </InputSelect>
        )}
        {mode === "filter" && (
          <Stack direction="row" gap={0.5} alignItems="center" sx={{ pl: 1 }}>
            <Typography variant="caption">
              {t("customerportal.pages.promotions.filter.title")}
            </Typography>
            <ScreenSelectFilter
              groups={groups}
              premises={[]}
              values={filters}
              onChange={handleFilterChange}
            />
          </Stack>
        )}

        <Stack
          direction="row"
          gap={0.5}
          alignItems="center"
          justifyContent="flex-end"
        >
          <Typography variant="caption">
            {Object.values(groupedScreens).length > 1
              ? t(
                  `customerportal.pages.promotions.select_all_screens_on_${selectedGrouping}`
                )
              : t("customerportal.pages.promotions.select_all_screens")}
          </Typography>
          {screens.length > 0 && (
            <Switch
              checked={selectedScreens.length === filteredScreens.length}
              onChange={handleSelectAll}
            />
          )}
        </Stack>
      </Stack>

      {/* accordions */}
      {Object.entries(groupedScreens).map(([groupKey, scrs]) => {
        const nrOfSelectedScreensInGroup = selectedScreens.filter((s) =>
          scrs.some((scr) => scr._id === s._id)
        ).length;

        return (
          <Accordion
            key={groupKey}
            expanded={expanded.includes(groupKey)}
            defaultExpanded
          >
            <AccordionSummary
              sx={{ display: "flex" }}
              expandIcon={<ExpandMore onClick={() => handleExpand(groupKey)} />}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Box onClick={() => handleExpand(groupKey)}>
                  <Typography variant="body1">
                    {groupedByLabel?.[groupKey] ?? groupKey}
                  </Typography>
                  <Typography variant="body1" fontSize="0.8rem" color="gray">
                    {`${t("glossary.unitWithCount", { count: scrs.length })} (${t(
                      "glossary.selectedWithCount",
                      { count: nrOfSelectedScreensInGroup }
                    )})`}
                  </Typography>
                </Box>

                <Box onClick={() => handleExpand(groupKey)} sx={{ flex: 1 }} />

                <Box sx={{ px: 1 }}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    label={t("common.select_all")}
                    labelPlacement="end"
                    control={
                      <Checkbox
                        onChange={() => handleSelectAllFromGroup(groupKey)}
                        checked={scrs.every((s) =>
                          selectedScreens.some((sel) => sel._id === s._id)
                        )}
                      />
                    }
                  />
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails
              sx={{
                p: 1,
                borderRadius: theme.shape.borderRadius,
                background: theme.palette.transparent.purple,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 0.5,
                maxHeight: "20rem",
                overflowY: "scroll",
                "&::-webkit-scrollbar": { backgroundColor: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  background: theme.palette.gradient.lightPurple,
                  borderRadius: 0.5,
                },
              }}
            >
              {scrs.map((screen) => (
                <ScreenCardSelect
                  key={screen._id}
                  screen={screen}
                  isSelected={selectedScreens.some((s) => s._id === screen._id)}
                  onChange={handleScreenSelect}
                />
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

interface ScreenCardSelectProps {
  screen: IRoom;
  isSelected: boolean;
  onChange: (screen: IRoom, selected: boolean) => void;
}

const ScreenCardSelect: React.FC<ScreenCardSelectProps> = ({
  screen,
  isSelected,
  onChange,
}) => {
  const theme = useTheme();

  const handleSelect = () => {
    onChange(screen, !isSelected);
  };

  return (
    <Box
      onClick={handleSelect}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
        background: isSelected
          ? theme.palette.transparent.blue
          : theme.palette.common.white,
        borderRadius: theme.shape.borderRadius,
        transition: "all 0.15s ease",
        p: "0.5rem",
        outline: `2px solid ${
          isSelected ? theme.palette.primary.light : "transparent"
        }`,
        "& .checkbox": {
          opacity: isSelected ? "1" : "0",
        },
        "&:hover": {
          outline: `2px solid ${theme.palette.primary.light}`,
          "& .checkbox": {
            opacity: "1",
          },
        },
      }}
    >
      <Typography variant="body1">{screen.name}</Typography>
      <Checkbox
        checked={isSelected}
        className="checkbox"
        sx={{
          padding: 0,
          color: theme.palette.primary.light + " !important",
          fill: theme.palette.primary.light + " !important",
        }}
      />
    </Box>
  );
};

export default ScreenSelect;
